import { useState } from "react";
import { Link } from "react-router-dom";
import { type Release, type ReleaseArtifact, formatBytes } from "@/lib/api";
import { formatDate } from "@/lib/time";
import { ReleaseNotesBody } from "./ReleaseNotesBody";

/**
 * One download row for a release artifact. Shows the filename, size,
 * and a truncated sha256 that copies the full digest to the clipboard
 * when clicked — critical for operators who want to verify downloads
 * before installing.
 */
function ArtifactRow({ artifact }: { artifact: ReleaseArtifact }) {
  const [copied, setCopied] = useState(false);
  const shortSha = artifact.sha256 ? artifact.sha256.slice(0, 12) : "";

  function handleCopySha(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!artifact.sha256) return;
    void navigator.clipboard.writeText(artifact.sha256);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors group">
      <a href={artifact.url} className="flex items-center gap-2 min-w-0 flex-1">
        <svg
          className="w-4 h-4 text-zinc-500 group-hover:text-blue-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        <span className="text-sm text-zinc-300 group-hover:text-zinc-100 truncate">
          {artifact.filename || artifact.name}
        </span>
      </a>
      <div className="flex items-center gap-2 flex-shrink-0">
        {shortSha && (
          <button
            type="button"
            onClick={handleCopySha}
            title={`sha256 ${artifact.sha256}\nClick to copy`}
            className={`font-mono text-[10px] px-1.5 py-0.5 rounded transition-colors ${
              copied
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-zinc-900 text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {copied ? "copied" : shortSha}
          </button>
        )}
        {artifact.size > 0 && (
          <span className="text-xs text-zinc-500">
            {formatBytes(artifact.size)}
          </span>
        )}
      </div>
    </div>
  );
}

interface ReleaseCardProps {
  release: Release;
  userVersion?: string;
  /**
   * Controls visual prominence. `bundle` gets the hero treatment
   * (larger, blue accent, componentVersions chips). `component` is the
   * smaller supporting card. Defaults by `release.component`.
   */
  variant?: "bundle" | "component";
}

export function ReleaseCard({ release, userVersion, variant }: ReleaseCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isCurrentVersion = userVersion === release.version;
  const resolvedVariant: "bundle" | "component" =
    variant ?? (release.component === "bundle" ? "bundle" : "component");
  const isBundle = resolvedVariant === "bundle";

  // Customer notes are the default body; internal notes sit behind a
  // toggle. Per-component rows never show `customerNotes` so we fall
  // straight to `notes`.
  const primaryNotes = isBundle
    ? release.customerNotes || release.notes
    : release.notes;

  return (
    <div
      className={`bg-zinc-900 border rounded-xl transition-colors ${
        isBundle
          ? release.isLatest
            ? "border-blue-500/40 ring-1 ring-blue-500/20 shadow-lg shadow-blue-500/5"
            : "border-blue-500/20 hover:border-blue-500/30"
          : release.isLatest
            ? "border-zinc-700 ring-1 ring-zinc-700/40"
            : "border-zinc-800 hover:border-zinc-700"
      }`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full text-left flex items-center justify-between gap-4 ${
          isBundle ? "px-6 py-5" : "px-5 py-4"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          {isBundle && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/25">
              Bundle
            </span>
          )}
          <h3
            className={`font-semibold text-zinc-100 font-mono ${
              isBundle ? "text-lg" : ""
            }`}
          >
            v{release.version}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {release.isLatest && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Latest
              </span>
            )}
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                release.channel === "stable"
                  ? "bg-green-500/10 text-green-400 border border-green-500/20"
                  : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
              }`}
            >
              {release.channel}
            </span>
            {isBundle &&
              release.rolloutPercent !== null &&
              release.rolloutPercent < 100 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-300 border border-purple-500/20">
                  {release.rolloutPercent}% rollout
                </span>
              )}
            {!isBundle && release.component && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-800 text-zinc-400 border border-zinc-700/50">
                {release.component}
              </span>
            )}
            {isCurrentVersion && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-700 text-zinc-300">
                Your version
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm text-zinc-500 hidden sm:inline">
            {formatDate(release.date)}
          </span>
          <svg
            className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isBundle && release.componentVersions && !expanded && (
        <div className="px-6 pb-4 flex flex-wrap gap-1.5 -mt-1">
          {Object.entries(release.componentVersions).map(([name, ver]) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono bg-zinc-800/70 text-zinc-400 border border-zinc-800"
            >
              <span className="text-zinc-500">{name}</span>
              <span className="text-zinc-300">{ver}</span>
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div
          className={`border-t border-zinc-800 pt-4 space-y-4 animate-fade-in ${
            isBundle ? "px-6 pb-6" : "px-5 pb-5"
          }`}
        >
          <p className="text-sm text-zinc-500 sm:hidden">
            {formatDate(release.date)}
          </p>

          {isBundle && release.componentVersions && (
            <div>
              <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">
                Pinned components
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(release.componentVersions).map(
                  ([name, ver]) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-mono bg-zinc-800 text-zinc-300 border border-zinc-700/50"
                    >
                      <span className="text-zinc-500">{name}</span>
                      <span className="text-zinc-100">{ver}</span>
                    </span>
                  ),
                )}
              </div>
            </div>
          )}

          <ReleaseNotesBody notes={primaryNotes} />

          {release.artifacts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Downloads
              </h4>
              <div className="space-y-1.5">
                {release.artifacts.map((artifact) => (
                  <ArtifactRow key={artifact.name} artifact={artifact} />
                ))}
              </div>
            </div>
          )}

          <Link
            to={
              isBundle
                ? `/releases/${release.channel}/${release.version}`
                : release.component
                  ? `/releases/${release.channel}/${release.version}?component=${release.component}`
                  : `/releases/${release.channel}/${release.version}`
            }
            className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            Full release details
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      )}
    </div>
  );
}
